import { registerLocaleData, AsyncPipe } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { Component, HostListener, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
  Event as RouterEvent,
} from '@angular/router';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { HelperService } from '@shared/services/helper.service';
import { InputNavigationService } from '@app/shared/services/input-navigation.service';
import { LoggerService } from '@shared/services/logger.service';
import { ModalManagerService } from '@shared/services/modal-manager.service';
import { AppService } from './app.service';
import { Settings } from './app.settings';
import { TokenService } from '@shared/services/token.service';
import { RouteParameterInterceptor } from '@shared/services/routeParameterInterceptor.service';
import { EventManager } from '@shared/utils/event-manager';
import { SessionIdService } from '@shared/services/sessionId.service';
import { DeviceService } from '@shared/services/device.service';
import { NativeService } from '@shared/services/native.service';
import { Modal, ModalType, NonPersistedModalName } from '@shared/models/modal.model';
import { HandleErrorsService } from '@shared/services/handleErrors.service';
import { ToastrService } from '@shared/services/toastr.service';
import { SignalREvents } from '@shared/services/signalR-events.service';
import { LandingService } from './landing/landing.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EnumUnlockedFeature, User } from './shared/models/user.model';
import { GoogleTagManagerService } from './shared/services/gtm.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { AudioService } from './audio/audio.service';
import { AUDIO_LIBRARY } from './audio/audio.definitions';
import { UserRepository } from './shared/state/user/user.repository';
import { VersionRepository } from './shared/state/version/version.repository';
import { UserService } from './shared/services/user.service';
import { GameOrder } from './shared/models/lobby.model';
import { GlobalDataRepository } from './shared/state/global-data/global-data.repository';
import { WarningLandscapeComponent } from './shared/components/warning-landscape/warning-landscape.component';
import { CookieConsentComponent } from './shared/utils/cookie-consent/cookie-consent.component';
import { ShopPaymentProviderComponent } from './home/shop/shop-payment-provider/shop-payment-provider.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { PreLoaderComponent } from './shared/components/pre-loader/pre-loader.component';
import { FirehoseAnalyticsService } from './core/analytics/firehose/firehose-analytics.service';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    RouterOutlet,
    AsyncPipe,
    WarningLandscapeComponent,
    CookieConsentComponent,
    ShopPaymentProviderComponent,
    LoadingComponent,
    PreLoaderComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  @HostListener('window:resize') onResizeScreen() {
    this.setFontSizeOnMobileBrowser();
  }

  loaded = signal(false);
  componentDestroy: () => Observable<unknown>;

  // using this variable on slotcom-header component
  leaveDate: number;
  entryDate: number;
  private allowSleep: (_: unknown) => void;

  @HostListener('document:visibilitychange') onFocus() {
    if (document.hidden) {
      this.leaveDate = Date.now();
    } else {
      this.entryDate = Date.now();
      const elapsedTime = this.entryDate - this.leaveDate;

      if (elapsedTime >= Settings.idleRefreshTime) {
        this.appService.reload();
      }
    }
  }

  get user(): User {
    return this.userRepo.getUser();
  }

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly appService = inject(AppService);
  private readonly landingService = inject(LandingService);
  private readonly helper = inject(HelperService);
  protected readonly eventManager = inject(EventManager);
  private readonly tokenService = inject(TokenService);
  private readonly userRepo = inject(UserRepository);
  private readonly versionRepo = inject(VersionRepository);
  private readonly modalManagerService = inject(ModalManagerService);
  private readonly routeParameterInterceptor = inject(RouteParameterInterceptor);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly firehoseAnalyticsService = inject(FirehoseAnalyticsService);
  protected readonly nativeService = inject(NativeService);
  public readonly logger = inject(LoggerService);
  private readonly handleErrorsService = inject(HandleErrorsService);
  private readonly signalREventsService = inject(SignalREvents);
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly audioService = inject(AudioService);
  private readonly userService = inject(UserService);
  private readonly globalDataRepo = inject(GlobalDataRepository);
  private readonly keyboardNavigationService = inject(InputNavigationService);

  constructor() {
    inject(SessionIdService);
    inject(GoogleTagManagerService);
    inject(DeviceService);
    inject(ToastrService);

    this.registerLocaleData();
  }

  async ngOnInit() {
    await this.handleErrorsService.init();
    await this.appService.init();

    await this.analyticsService.logEvent('ps_angular_init');
    this.firehoseAnalyticsService.track('app_init');

    this.audioService.registerAudios(Object.values(AUDIO_LIBRARY));
    this.versionRepo.updateVersion({ frontend: environment.VERSION });
    this.modalManagerService.initAllSubscriptions();
    this.keyboardNavigationService.init();
    this.signalREventsService.subscribeSignalREvents();
    this.setFontSizeOnMobileBrowser();
    this.setTitleOnNavigate();
    await this.routeUserToInitialPage();
    this.checkUpdateVersion();
    this.setupNavigationListener();
    this.manageUserFilters();

    this.eventManager.appLoaded(true);
    this.avoidSleep();
    this.loaded.set(true);
  }

  openTesterPanel(): void {
    this.eventManager.openModal(new Modal(NonPersistedModalName.TesterModalComponent, ModalType.View));
  }

  private async routeUserToInitialPage() {
    const tokenExists = await this.existingToken();

    if (tokenExists) {
      this.firehoseAnalyticsService.track('af_login');

      await this.userService.fetchCurrentUser();

      if (this.user.emailConfirmed) {
        this.firehoseAnalyticsService.track('login_ok', {
          auth_type: 'email',
        });
        await this.landingService.showLobby();
      } else {
        if (this.appService.emailConfirmation) {
          await this.landingService.showEmailValidated();
        } else {
          await this.landingService.showValidateEmail(this.user.email);
        }
      }
    } else {
      if (this.appService.emailConfirmation) {
        await this.landingService.showEmailValidated();
      } else if (this.appService.resetPassword) {
        await this.landingService.showResetPassword();
      } else {
        await this.landingService.showLanding();
      }
    }
  }

  private async existingToken(): Promise<boolean> {
    if (this.tokenService.hasToken()) {
      return await this.tokenService.refreshIfNecessaryToken();
    } else {
      return false;
    }
  }

  // This is used for register local data for translate local pipes (ES & FR for DecimalPipe for example)
  private registerLocaleData() {
    registerLocaleData(localeEs);
    registerLocaleData(localeFr);
  }

  private setTitleOnNavigate() {
    this.router.events
      .pipe(
        filter((e: Event | RouterEvent) => e instanceof NavigationEnd),
        map(() => this.route.snapshot),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .subscribe({
        next: (route: ActivatedRouteSnapshot) => {
          this.routeParameterInterceptor.checkRouteParameters(route);
        },
        error: (error) => this.logger.error(error),
      });
  }

  private checkUpdateVersion() {
    if (!this.nativeService.isNative()) return;

    this.helper
      .fetchNativeVersions()
      .pipe(filter((nativeVersions) => this.nativeService.isOutdated(nativeVersions)))
      .subscribe(() => {
        this.eventManager.closeAllModals();
        this.modalManagerService.openUpdateVersionModal();
      });
  }

  private manageUserFilters() {
    this.userRepo.unlockedFeatures$.pipe(takeUntil(this.componentDestroy())).subscribe((unlockedFeatures) => {
      if (!unlockedFeatures?.includes(EnumUnlockedFeature.TagsAndFilters)) {
        this.globalDataRepo.updateGlobalData({
          gamesOrder: GameOrder.ONBOARDING,
        });
      } else {
        this.globalDataRepo.updateGlobalData({
          gamesOrder: GameOrder.DEFAULT,
        });
      }
    });
  }

  private setupNavigationListener() {
    this.router.events
      .pipe(
        filter((event: RouterEvent) => this.isNavigationEvent(event)),
        takeUntil(this.componentDestroy())
      )
      .subscribe({
        next: (event) => {
          this.eventManager.setIsNavigating(event instanceof NavigationStart);
          if (event instanceof NavigationError) {
            this.logger.error('Navigation error:', event.error);
          }
        },
        error: this.logger.catch(),
      });
  }

  private isNavigationEvent(event: RouterEvent): boolean {
    return (
      event instanceof NavigationStart ||
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    );
  }

  private avoidSleep() {
    if (navigator && navigator.locks && navigator.locks.request) {
      const promise = new Promise((res) => {
        this.allowSleep = res;
      });

      navigator.locks.request('unique_lock_name', { mode: 'shared' }, () => {
        return promise;
      });
    }
  }

  // For browser mobile, still working
  private setFontSizeOnMobileBrowser() {
    if (this.deviceDetectorService.isDesktop() || this.nativeService.isNative()) return;

    this.setFontSize();
  }

  private setFontSize() {
    document.documentElement.style.fontSize = '';
    document.documentElement.style.fontSize = `${Math.min(window.innerHeight, window.innerWidth) * 0.03}px`;
  }

  ngOnDestroy() {
    if (this.allowSleep) this.allowSleep(undefined);
  }
}
